import type { Block, BlockStyle } from "@alchimie/shared";
import { COLORS, Field, Input } from "../ui.js";

interface Props {
  style: BlockStyle | undefined;
  onChange: (style: BlockStyle) => void;
  /** Type du bloc : permet de cacher les sections non pertinentes
   *  (texte/conteneur/animation pour un séparateur, etc.). */
  blockType?: Block["type"];
}

// Configuration fine par type de bloc : ce qui apparaît dans le panneau Style.
// Tout ce qui n'a pas d'effet visible sur le rendu est masqué pour éviter le bruit.
type StyleSections = {
  spacing: boolean;        // paddings
  background: boolean;     // couleur/image/overlay
  textColor: boolean;
  subtitleColor: boolean;  // pertinent uniquement quand le rendu utilise subtitleColor()
  textAlign: boolean;
  fontFamily: boolean;
  container: boolean;      // maxWidth + borderRadius
  animation: boolean;
};

const FULL: StyleSections = {
  spacing: true, background: true, textColor: true, subtitleColor: true,
  textAlign: true, fontFamily: true, container: true, animation: true,
};

const STYLE_CONFIG: Record<Block["type"], StyleSections> = {
  HERO:         FULL,
  RICH_TEXT:    { ...FULL, subtitleColor: false },
  VIDEO:        FULL,
  AUDIO:        { ...FULL, textColor: false, subtitleColor: false, textAlign: false, fontFamily: false },
  PDF:          { ...FULL, textAlign: false, fontFamily: false },
  IMAGE:        { ...FULL, textColor: false, subtitleColor: false, textAlign: false, fontFamily: false },
  BENEFITS:     FULL,
  TESTIMONIALS: FULL,
  FAQ:          FULL,
  PRICING:      { ...FULL, textColor: false, subtitleColor: false, textAlign: false, fontFamily: false },
  CTA:          FULL,
  COLUMNS:      { ...FULL, textColor: false, subtitleColor: false, textAlign: false, fontFamily: false },
  CUSTOM_HTML:  { ...FULL, textColor: false, subtitleColor: false, textAlign: false, fontFamily: false, animation: false },
  // Cas dédiés gérés plus haut :
  DIVIDER:      { spacing: true, background: false, textColor: false, subtitleColor: false, textAlign: false, fontFamily: false, container: false, animation: false },
  SPACER:       { spacing: false, background: true, textColor: false, subtitleColor: false, textAlign: false, fontFamily: false, container: false, animation: false },
};

function sectionsFor(blockType?: Block["type"]): StyleSections {
  if (!blockType) return FULL;
  return STYLE_CONFIG[blockType] ?? FULL;
}

/**
 * Édition du style universel d'un bloc (paddings, fond, texte, conteneur,
 * animations). Toutes les valeurs sont des chaînes CSS (ex : "32px", "1rem").
 */
export function StyleEditor({ style, onChange, blockType }: Props) {
  const s = style ?? {};
  function set<K extends keyof BlockStyle>(key: K, value: BlockStyle[K]) {
    onChange({ ...s, [key]: value });
  }

  const sec = sectionsFor(blockType);

  // Cas SPACER : panneau ultra-minimal — uniquement une couleur de bande.
  if (blockType === "SPACER") {
    return (
      <div>
        <Group label="Fond">
          <Field label="Couleur de la bande">
            <ColorInput value={s.backgroundColor} onChange={(v) => set("backgroundColor", v)} />
          </Field>
        </Group>
      </div>
    );
  }

  // Si aucune section n'est activée, message minimal.
  if (!sec.spacing && !sec.background && !sec.textColor && !sec.subtitleColor && !sec.textAlign && !sec.fontFamily && !sec.container && !sec.animation) {
    return (
      <div style={{ padding: 24, color: COLORS.muted, fontStyle: "italic", fontSize: 13, textAlign: "center" }}>
        Aucune option de style pour ce bloc.
      </div>
    );
  }

  // Le bloc Texte (Couleur, Sous-titre, Alignement, Police) n'apparaît que si
  // au moins un de ses champs est activé.
  const showTextGroup = sec.textColor || sec.subtitleColor || sec.textAlign || sec.fontFamily;

  return (
    <div>
      {sec.spacing && (
      <Group label="Espacement">
        <Pair>
          <Field label="Padding haut">
            <Input
              value={s.paddingTop ?? ""}
              onChange={(e) => set("paddingTop", e.target.value || undefined)}
              placeholder="32px"
            />
          </Field>
          <Field label="Padding bas">
            <Input
              value={s.paddingBottom ?? ""}
              onChange={(e) => set("paddingBottom", e.target.value || undefined)}
              placeholder="32px"
            />
          </Field>
        </Pair>
        <Pair>
          <Field label="Padding gauche">
            <Input
              value={s.paddingLeft ?? ""}
              onChange={(e) => set("paddingLeft", e.target.value || undefined)}
              placeholder="0"
            />
          </Field>
          <Field label="Padding droit">
            <Input
              value={s.paddingRight ?? ""}
              onChange={(e) => set("paddingRight", e.target.value || undefined)}
              placeholder="0"
            />
          </Field>
        </Pair>
      </Group>
      )}

      {sec.background && (
        <Group label="Fond">
          <Field label="Couleur de fond">
            <ColorInput value={s.backgroundColor} onChange={(v) => set("backgroundColor", v)} />
          </Field>
          <Field label="Image de fond (URL)">
            <Input
              value={s.backgroundImage ?? ""}
              onChange={(e) => set("backgroundImage", e.target.value || undefined)}
              placeholder="https://…"
            />
          </Field>
          {s.backgroundImage && (
            <Field label="Overlay (rgba)">
              <Input
                value={s.backgroundOverlay ?? ""}
                onChange={(e) => set("backgroundOverlay", e.target.value || undefined)}
                placeholder="rgba(0,0,0,0.4)"
              />
            </Field>
          )}
        </Group>
      )}

      {showTextGroup && (
      <Group label="Texte">
        {sec.textColor && (
          <Field label="Couleur du texte">
            <ColorInput value={s.textColor} onChange={(v) => set("textColor", v)} />
          </Field>
        )}
        {sec.subtitleColor && (
          <Field label="Couleur du sous-titre">
            <ColorInput value={s.subtitleColor} onChange={(v) => set("subtitleColor", v)} />
          </Field>
        )}
        {sec.textAlign && (
          <Field label="Alignement">
            <div style={{ display: "flex", gap: 4 }}>
              {(["left", "center", "right"] as const).map((a) => (
                <button
                  key={a}
                  onClick={() => set("textAlign", a)}
                  style={{
                    flex: 1,
                    padding: "8px",
                    border: `1px solid ${s.textAlign === a ? COLORS.accent : COLORS.border}`,
                    background: s.textAlign === a ? COLORS.accent : "transparent",
                    color: s.textAlign === a ? "#fff" : COLORS.text,
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 13,
                    fontFamily: "inherit",
                  }}
                >
                  {a === "left" ? "← Gauche" : a === "center" ? "Centre" : "Droite →"}
                </button>
              ))}
            </div>
          </Field>
        )}
        {sec.fontFamily && (
          <Field label="Police (font-family)">
            <Input
              value={s.fontFamily ?? ""}
              onChange={(e) => set("fontFamily", e.target.value || undefined)}
              placeholder="ex : Inter, serif…"
            />
          </Field>
        )}
      </Group>
      )}

      {sec.container && (
      <Group label="Conteneur">
        <Pair>
          <Field label="Largeur max">
            <Input
              value={s.maxWidth ?? ""}
              onChange={(e) => set("maxWidth", e.target.value || undefined)}
              placeholder="1080px"
            />
          </Field>
          <Field label="Rayon de bordure">
            <Input
              value={s.borderRadius ?? ""}
              onChange={(e) => set("borderRadius", e.target.value || undefined)}
              placeholder="0"
            />
          </Field>
        </Pair>
      </Group>
      )}

      {sec.animation && (
      <Group label="Animation d'entrée">
        <Field label="Type">
          <select
            value={s.enterAnimation ?? "none"}
            onChange={(e) => set("enterAnimation", e.target.value as BlockStyle["enterAnimation"])}
            style={selectStyle}
          >
            <option value="none">Aucune</option>
            <option value="fade">Fondu</option>
            <option value="slide-up">Glisse vers le haut</option>
            <option value="slide-left">Glisse vers la gauche</option>
            <option value="scale">Zoom</option>
          </select>
        </Field>
        {s.enterAnimation && s.enterAnimation !== "none" && (
          <Field label="Délai (ms)">
            <Input
              type="number"
              value={s.enterDelay ?? ""}
              onChange={(e) => set("enterDelay", e.target.value ? Number(e.target.value) : undefined)}
              placeholder="0"
            />
          </Field>
        )}
      </Group>
      )}
    </div>
  );
}

export function ColorInput({ value, onChange }: { value: string | undefined; onChange: (v: string | undefined) => void }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <input
        type="color"
        value={value && value.startsWith("#") ? value : "#000000"}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 40, height: 36, border: `1px solid ${COLORS.border}`, borderRadius: 6, cursor: "pointer", background: "transparent" }}
      />
      <Input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || undefined)}
        placeholder="transparent"
        style={{ flex: 1 }}
      />
    </div>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function Pair({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>{children}</div>;
}

const selectStyle = {
  width: "100%",
  padding: "8px 10px",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 6,
  fontSize: 14,
  fontFamily: "inherit",
};
